import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatBadge } from '@angular/material/badge'
import { MatButton } from '@angular/material/button'
import { MatChip } from '@angular/material/chips'
import { ThemePalette } from '@angular/material/core'
import { MatDialog } from '@angular/material/dialog'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenuItem } from '@angular/material/menu'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs'
import { MatTooltip } from '@angular/material/tooltip'
import { ActivatedRoute, Router } from '@angular/router'
import { AsyncOutputJob, OutputJobIndicatorComponent } from '@elderbyte/ngx-imprimo'
import {
  DataContextBuilder,
  ElderBasicPaneLayoutComponent,
  ElderDataToolbarComponent,
  ElderFormFieldDenseDirective,
  ElderFormFieldLabelDirective,
  ElderHeaderComponent,
  ElderNumberCellDirective,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderQuantityPipe,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToastService,
  ElderToolbarContentDirective,
  ElderTranslatePipe,
  ElderTranslateService,
  IDataContext,
  Sort,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription, timer } from 'rxjs'
import { debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators'
import { truthyPredicate } from '../../../../../../functional.util'
import { ProductSalesQuantityPipe } from '../../../../../features/pipes/product-sales-quantity.pipe'
import { ProductSalesTotalWeightPipe } from '../../../../../features/pipes/product-total-weight.pipe'
import {
  BatchTracingFilter,
  BatchTracingService,
} from '../../../../batchtracing/batch-tracing.service'
import {
  ITracedMaterialBrowserDialogOptions,
  TracedMaterialBrowserDialogComponent,
} from '../../../../batchtracing/tracedmaterial/traced-material-browser-dialog/traced-material-browser-dialog.component'
import {
  ReportState,
  WorkBatch,
  WorkBatchLine,
  WorkBatchSegment,
  WorkBatchSegmentPackagingMode,
  WorkBatchSegmentState,
  WorkBatchState,
} from '../../../../orders/batches/work-batch'
import { WorkBatchLineComponent } from '../../../../orders/batches/work-batch-line/work-batch-line.component'
import { ItemManufacturingStatusComponent } from '../../../../orders/work-items/item-manufacturing-status/item-manufacturing-status.component'
import {
  WorkItemPack,
  WorkItemPackagesComponent,
} from '../../../../orders/work-items/packaging/work-item-packages/work-item-packages.component'
import { WorkItem, WorkItemUtils, WorkTransferState } from '../../../../orders/work-items/work-item'
import { WorkItemFilter, WorkItemService } from '../../../../orders/work-items/work-item.service'
import { WorkOrderStorageLocationOverview } from '../../../../orders/work-orders/work-order'
import { WorkOrderService } from '../../../../orders/work-orders/work-order.service'
import { OutputPreviewService } from '../../../../output/preview/output-preview.service'
import { ReportSpec } from '../../../../output/report/report-job'
import { ReportJobService } from '../../../../output/report/report-job.service'
import { Package } from '../../../../packages/package'
import {
  ProductionListBatchContext,
  ProductionListPreviewService,
} from '../../../../production/lists/production-list-preview.service'
import {
  BatchItemLabelProvisioning,
  WorkCenter,
} from '../../../../shopfloor/workcenters/work-center'
import { WorkCenterService } from '../../../../shopfloor/workcenters/work-center.service'
import { WorkStation } from '../../../../shopfloor/workstations/work-station'
import { WorkStationService } from '../../../../shopfloor/workstations/work-station.service'
import { StoragePath } from '../../../../storage/storage-path'
import { ControlTerminal } from '../../../control-terminal'
import { TerminalBatchService } from '../../batches/terminal-batch.service'
import {
  NewBatchSegmentJob,
  WorkSegmentCommand,
  WorkSegmentCommandOptions,
} from '../../segments/batch-job'
import { TerminalBatchJobService } from '../../segments/terminal-batch-job.service'
import { WorkBatchStateUtil } from '../../work-batch-state-util'
import { BatchTracingCtx } from '../batch-tracing-ctx'
import { TerminalBatchPackagesComponent } from '../terminal-batch-packages/terminal-batch-packages.component'
import {
  TerminalOrderPackageDetailDialogueComponent,
  TerminalOrderPackageDialogueOptions,
} from '../terminal-orders-package-detail/terminal-order-package-detail-dialogue/terminal-order-package-detail-dialogue.component'
import { TerminalWorkBatch } from '../terminal-work-batch'
import { TerminalWorkBatchCtx } from '../terminal-work-batch-ctx'
import {
  ITerminalWorkItemDialogOptions,
  TerminalWorkItemDialogComponent,
} from '../terminal-work-item-dialog/terminal-work-item-dialog.component'
import { WorkOrderStorageLocationUtil } from '../work-order-storage-location-util'
import { BatchStatusIndicatorComponent } from './batch-status-indicator/batch-status-indicator.component'
import { ProductionHintComponent } from './production-hint/production-hint.component'

@Component({
  selector: 'app-control-terminal-batch-detail',
  templateUrl: './control-terminal-batch-detail.component.html',
  styleUrls: ['./control-terminal-batch-detail.component.scss'],
  imports: [
    ElderHeaderComponent,
    MatChip,
    MatIcon,
    MatFormField,
    ElderFormFieldLabelDirective,
    ElderFormFieldDenseDirective,
    MatLabel,
    MatInput,
    FormsModule,
    MatButton,
    WorkBatchLineComponent,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    MatTooltip,
    MatBadge,
    MatMenuItem,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    ElderNumberCellDirective,
    WorkItemPackagesComponent,
    ItemManufacturingStatusComponent,
    TerminalBatchPackagesComponent,
    AsyncPipe,
    ElderTranslatePipe,
    ElderQuantityPipe,
    ProductSalesQuantityPipe,
    ProductSalesTotalWeightPipe,
    OutputJobIndicatorComponent,
    BatchStatusIndicatorComponent,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneContentComponent,
    ProductionHintComponent,
  ],
})
export class ControlTerminalBatchDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  // Terminal
  public readonly workCenterId$ = new BehaviorSubject<string | null>(null)
  public readonly workStationId$ = new BehaviorSubject<string | null>(null)
  public readonly workOrderId$ = new BehaviorSubject<string | null>(null)
  public readonly workBatchId$ = new BehaviorSubject<string | null>(null)

  public readonly terminalBatch$ = new BehaviorSubject<TerminalWorkBatch | null>(null)
  public readonly workStation$ = new BehaviorSubject<WorkStation | null>(null)
  public readonly workCenter$ = new BehaviorSubject<WorkCenter | null>(null)
  public readonly workBatchSegment$ = new BehaviorSubject<WorkBatchSegment | null>(null)

  public readonly controlTerminal$ = new BehaviorSubject<ControlTerminal | null>(null)

  public readonly terminalWorkBatchCtx$ = new BehaviorSubject<TerminalWorkBatchCtx | null>(null)

  // Printing
  public readonly asyncJob$ = new BehaviorSubject<AsyncOutputJob | null>(null)
  public readonly labelReport$: Observable<ReportSpec>

  // Storage and Packages
  public readonly storageScope$ = new BehaviorSubject<StoragePath | null>(null)
  public readonly storageOverview$ = new BehaviorSubject<WorkOrderStorageLocationOverview | null>(
    null
  )

  // Batch Tracing
  public readonly batchTracingCtx$ = new BehaviorSubject<BatchTracingCtx | null>(null)

  // WorkItems
  public readonly data: IDataContext<WorkItem>

  // Polling
  private batchPollingTerminator$ = new Subject<void>()
  private batchPoller$: Subscription

  // Enums
  public readonly BatchState = WorkBatchState
  public readonly TransferState = WorkTransferState

  public selectedTabIndex = 0

  protected readonly Math = Math

  dialog = inject(MatDialog)

  private readonly transferSubject$ = new Subject<NewBatchSegmentJob>()

  private readonly toastService = inject(ElderToastService)
  private readonly workStationService = inject(WorkStationService)
  private readonly workCenterService = inject(WorkCenterService)
  private readonly workOrderService = inject(WorkOrderService)
  private readonly terminalBatchService = inject(TerminalBatchService)
  private readonly terminalBatchJobService = inject(TerminalBatchJobService)
  private readonly outputTaskService = inject(ReportJobService)
  private readonly outputPreviewService = inject(OutputPreviewService)
  private readonly translate = inject(ElderTranslateService)
  private router = inject(Router)
  private route = inject(ActivatedRoute)
  private matDialog = inject(MatDialog)
  private prodListPreviewService = inject(ProductionListPreviewService)
  private readonly batchTracingService = inject(BatchTracingService)
  private readonly destroyRef = inject(DestroyRef)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const workItemService = inject(WorkItemService)

    this.data = DataContextBuilder.start<WorkItem>().pageSize(100).build(workItemService)

    const controlTerminal = this.route?.parent?.parent?.snapshot?.data[
      'controlTerminal'
    ] as ControlTerminal
    this.log.debug(
      `Got controlTerminal from grandparent route: ${controlTerminal?.controlCenterId}`
    )
    this.controlTerminal$.next(
      // we need to access 'grandparent' here
      controlTerminal
    )

    this.labelReport$ = this.terminalBatch$.pipe(
      filter((terminalBatch) => !!terminalBatch),
      map((terminalBatch) => ReportSpec.itemLabelsForBatch(terminalBatch.workBatch.id))
    )

    this.setupCombinedEvents()

    this.listenToRouteParams()

    if (this.route?.parent?.params) {
      this.route.parent.params
        .pipe(
          takeUntilDestroyed(),
          tap((parentParams) => {
            console.debug(`got parent params ${JSON.stringify(parentParams)}`)
          }),
          map((params) => params['workCenterId']),
          filter((id) => id)
        )
        .subscribe((workCenterId) => {
          this.workCenterId$.next(workCenterId)
        })
    }

    this.transferSubject$
      .pipe(
        takeUntilDestroyed(),
        debounceTime(200),
        switchMap((job) => this.terminalBatchJobService.createNew(job))
      )
      .subscribe({
        next: (_) => {
          this.toastService.info('Batch transferring ...')
          this.startTransferPolling()
        },
        error: (err) => {
          this.toastService.error('Failed to start batch transfer!', err)
          this.reloadWorkBatch()
        },
      })

    this.workBatchId$
      .pipe(
        takeUntilDestroyed(),
        filter(truthyPredicate),
        switchMap((workBatchId) => this.terminalBatchService.findById(workBatchId))
      )
      .subscribe({
        next: (terminalBatch) => {
          this.terminalBatch$.next(terminalBatch)
          this.workBatchSegment$.next(
            terminalBatch.workBatch.productionLine.segments.find(
              (s) => s.workCenterId === this.workCenterId$.getValue()
            ) ?? null
          )
        },
        error: (err) => this.toastService.error('Failed to load TerminalBatch!', err),
      })

    this.workStationId$
      .pipe(
        takeUntilDestroyed(),
        filter(truthyPredicate),
        switchMap((workStationId) => this.workStationService.findById(workStationId))
      )
      .subscribe({
        next: (workStation) => this.workStation$.next(workStation),
        error: (err) => this.toastService.error('Failed to load WorkStation!', err),
      })

    this.workCenterId$
      .pipe(
        takeUntilDestroyed(),
        filter(truthyPredicate),
        switchMap((workCenterId) => this.workCenterService.findById(workCenterId))
      )
      .subscribe({
        next: (workCenter) => this.workCenter$.next(workCenter),
        error: (err) => this.toastService.error('Failed to load WorkCenter!', err),
      })

    this.workBatchSegment$.pipe(takeUntilDestroyed()).subscribe((segment) => {
      const segmentJobId = segment?.itemLabelsReport?.jobId
      if (segmentJobId) {
        const currentJob = this.asyncJob$.getValue()?.jobSnapshot

        if (!currentJob || currentJob.id !== segmentJobId) {
          this.outputTaskService.findJobByIdAsAsync(segment.itemLabelsReport.jobId).subscribe({
            next: (asyncJob) => this.asyncJob$.next(asyncJob),
            error: (err) =>
              this.toastService.warn('Could not find Report Job ' + segment.itemLabelsReport.jobId),
          })
        }
      }
    })
  }

  private setupCombinedEvents(): void {
    combineLatest([this.workOrderId$, this.workBatchId$, this.workCenterId$])
      .pipe(takeUntilDestroyed())
      .subscribe(([orderId, workBatchId, workCenterId]) => {
        this.log.warn('order: ' + orderId + ', batch: ' + workBatchId + ', wc: ' + workCenterId)

        if (orderId && workCenterId && workBatchId) {
          this.data.start(
            [new Sort('productionRoute.sequence.processingSequencePos', 'asc')],
            [
              WorkItemFilter.workOrderId(orderId),
              WorkItemFilter.workCenterId(workCenterId),
              WorkItemFilter.workBatchId(workBatchId),
            ]
          )

          this.loadTracedMaterials(workBatchId, workCenterId)
        }
      })

    combineLatest([this.controlTerminal$, this.workCenter$, this.workStation$, this.terminalBatch$])
      .pipe(takeUntilDestroyed())
      .subscribe(([terminal, workCenter, workStation, terminalBatch]) => {
        if (terminal && workCenter && workStation && terminalBatch) {
          const ctx = new TerminalWorkBatchCtx(terminal, workCenter, workStation, terminalBatch)
          this.terminalWorkBatchCtx$.next(ctx)

          const scope = ctx.workCenter.packaging.defaultStoragePath
          this.storageScope = scope ? StoragePath.parse(scope) : null
          this.initStorageOverview()
        }
      })
  }

  private listenToRouteParams(): void {
    this.route.params.pipe(takeUntilDestroyed()).subscribe((params) => {
      const orderId = params['workOrderId']
      if (orderId) {
        this.workOrderId$.next(orderId)
      }

      const workBatchId = params['workBatchId']
      if (workBatchId) {
        this.workBatchId$.next(workBatchId)
      }
    })

    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe((queryParams) => {
      const workStationId = queryParams['workStationId']
      if (workStationId) {
        this.workStationId$.next(workStationId)
      }
    })
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    this.startPreparationPolling()
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get terminalWorkBatchCtx(): TerminalWorkBatchCtx | null {
    return this.terminalWorkBatchCtx$.getValue()
  }

  public get terminalBatch(): TerminalWorkBatch | null {
    return this.terminalBatch$.getValue()
  }

  public get workBatchSegment(): WorkBatchSegment | null {
    return this.workBatchSegment$.getValue()
  }

  public get controlTerminal(): ControlTerminal | null {
    return this.controlTerminal$.getValue()
  }

  public set storageScope(scope: StoragePath | null) {
    this.storageScope$.next(scope)
  }

  public get storageScope(): StoragePath | null {
    return this.storageScope$.getValue()
  }

  public get storageOverview(): WorkOrderStorageLocationOverview | null {
    return this.storageOverview$.getValue()
  }

  public get batchTracingCtx(): BatchTracingCtx | null {
    return this.batchTracingCtx$.getValue()
  }

  public get workCenterId(): string | null {
    return this.workCenterId$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API: WorkItem                                                    *
   *                                                                         *
   **************************************************************************/

  public getProductionPriority(item: WorkItem): number {
    return WorkItemUtils.getProcessingSequencePos(item, this.workBatchSegment?.workCenterId)
  }

  public getSortByProcessingSequencePosString(): string {
    return WorkItemUtils.getSortByProcessingSequencePosString(
      this.terminalBatch?.workBatch?.productionLine,
      this.workCenterId
    )
  }

  /***************************************************************************
   *                                                                         *
   * Public API: Batch                                                       *
   *                                                                         *
   **************************************************************************/

  public reloadWorkBatch(): void {
    this.log.debug('Triggering batch reload...')
    this.workBatchId$.next(this.workBatchId$.getValue())
  }

  public hasBeenPrepared(batch: WorkBatch): boolean {
    return (
      batch && batch.state !== WorkBatchState.PREVIEW && batch.state !== WorkBatchState.PREPARING
    )
  }

  public hasStarted(ctx: TerminalWorkBatchCtx): boolean {
    return ctx?.terminalBatch?.workBatch?.state === WorkBatchState.PROCESSING
  }

  public canStartBatch(batch: WorkBatch): boolean {
    if (batch) {
      switch (batch.state) {
        case WorkBatchState.PREVIEW:
        case WorkBatchState.PREPARING:
          return false
        case WorkBatchState.PREPARED:
        case WorkBatchState.PROCESSING:
          return (
            this.workBatchSegment?.state === WorkBatchSegmentState.OPEN &&
            WorkBatchStateUtil.previousSegmentStarted(
              this.terminalBatch.workBatch.productionLine.segments,
              this.workBatchSegment
            )
          )
        case WorkBatchState.COMPLETED:
          return false
        default:
          this.log.warn('Unknown WorkBatchState: ' + batch.state)
          return false
      }
    }
    return false
  }

  public startBatchSegment(): void {
    this.terminalBatchJobService.createNew(this.newBatchJob(WorkSegmentCommand.PROCESS)).subscribe({
      next: (_) => {
        this.toastService.info('Batch Processing ...')
        this.reloadWorkBatch()
        if (
          this.terminalWorkBatchCtx$.getValue() != null &&
          this.isItemLabelsCloudProvisioned(this.terminalWorkBatchCtx$.getValue())
        ) {
          this.startItemLabelPreparationPolling()
        }
      },
      error: (err) => {
        this.toastService.error('Failed to start batch!', err)
        this.reloadWorkBatch()
      },
    })
  }

  public refreshProductionGroups(): void {
    this.terminalBatchJobService
      .createNew(this.newBatchJob(WorkSegmentCommand.REFRESH_PROD_GROUPS))
      .subscribe({
        next: (_) => {
          this.toastService.info('Refreshing production groups ...')
          this.reloadWorkBatch()
        },
        error: (err) => {
          this.toastService.error('Failed to refresh production groups!', err)
          this.reloadWorkBatch()
        },
      })
  }

  public canProvideItemLabels(ctx: TerminalWorkBatchCtx): boolean {
    return (
      ctx.terminalBatch.workBatch.state === WorkBatchState.PROCESSING &&
      !this.isItemLabelsCloudProvisioned(ctx) &&
      !!ctx.terminal.labelPrinter?.printerId &&
      this.hasItemLabelsTemplate(ctx)
    )
  }

  public hasItemLabelsTemplate(ctx: TerminalWorkBatchCtx): boolean {
    return ctx.terminalBatch.hasItemLabels
  }

  private isItemLabelsCloudProvisioned(ctx: TerminalWorkBatchCtx): boolean {
    return (
      ctx.workCenter.labeling?.batchItemLabelProvisioning === BatchItemLabelProvisioning.CLOUD_FILE
    )
  }

  public canPreviewItemLabels(ctx: TerminalWorkBatchCtx): boolean {
    return ctx.terminalBatch.workBatch.state === WorkBatchState.PROCESSING
  }

  public onPreviewBatchLabels(ctx: TerminalWorkBatchCtx): void {
    this.outputPreviewService.openPreviewDialog(
      ReportSpec.workBatchLabels(ctx.terminalBatch.workBatch.id, ctx.workCenter.id)
    )
  }

  public onPreviewItemLabels(ctx: TerminalWorkBatchCtx): void {
    this.outputPreviewService.openPreviewDialog(
      ReportSpec.itemLabelsForBatch(ctx.terminalBatch.workBatch.id)
    )
  }

  public onPreviewPieceLabels(ctx: TerminalWorkBatchCtx): void {
    this.outputPreviewService.openPreviewDialog(
      ReportSpec.itemPieceLabelsForBatch(ctx.terminalBatch.workBatch.id)
    )
  }

  public onPreviewProdList(ctx: TerminalWorkBatchCtx): void {
    this.prodListPreviewService.previewProdList(
      new ProductionListBatchContext(ctx?.terminalBatch?.workBatch?.id, [])
    )
  }

  public provideItemLabels(): void {
    this.terminalBatchJobService
      .createNew(
        this.newBatchJob(
          WorkSegmentCommand.PROVIDE_ITEM_LABELS,
          WorkSegmentCommandOptions.Print(this.controlTerminal.labelPrinter.printerId)
        )
      )
      .subscribe({
        next: (_) => {
          this.toastService.info('Providing item labels ...')
          this.reloadWorkBatch()
        },
        error: (err) => {
          this.toastService.error('Failed to provide item labels!', err)
          this.reloadWorkBatch()
        },
      })
  }

  public itemLabelsPrepared(segment: WorkBatchSegment): boolean {
    return segment.itemLabelsReport?.jobState === ReportState.COMPLETED
  }

  public canTransferBatchSegment(ctx: TerminalWorkBatchCtx, segment: WorkBatchSegment): boolean {
    return (
      ctx.workStation?.gateway?.enabled &&
      segment.state === WorkBatchSegmentState.PROCESSING &&
      !segment.transferLock &&
      (this.isItemLabelsCloudProvisioned(ctx) ? this.itemLabelsPrepared(segment) : true)
    )
  }

  public transferBatchSegment(): void {
    // reset transfer state locally for button display
    if (this.workBatchSegment) {
      this.workBatchSegment.transferState = this.TransferState.SENDING
    }
    this.transferSubject$.next(this.newBatchJob(WorkSegmentCommand.TRANSFER))
  }

  public isLastSegment(ctx: TerminalWorkBatchCtx, segment: WorkBatchSegment) {
    return WorkBatchLine.isLastSegment(
      ctx.terminalBatch.workBatch.productionLine,
      segment.workCenterId
    )
  }

  public hasBeenTransferred(segment: WorkBatchSegment): boolean {
    return (
      (segment && segment.transferState === WorkTransferState.CONFIRMED) ||
      segment.transferState === WorkTransferState.FAILED
    )
  }

  public canCompleteBatchSegment(
    ctx: TerminalWorkBatchCtx,
    segment: WorkBatchSegment,
    packages: Package[],
    batchTracingCtx: BatchTracingCtx
  ): boolean {
    const segmentStateCheck = segment.state === WorkBatchSegmentState.PROCESSING

    const previousSegmentStateCheck = WorkBatchStateUtil.previousSegmentFinished(
      this.terminalBatch.workBatch.productionLine.segments,
      segment
    )

    let packagingCheck = true
    if (segment.packaging.mode !== WorkBatchSegmentPackagingMode.DISABLED) {
      packagingCheck = this.isPackagingOk(segment, packages)
    }

    const batchTracingCheck = batchTracingCtx ? batchTracingCtx.isConfigValid : false

    return segmentStateCheck && previousSegmentStateCheck && packagingCheck && batchTracingCheck
  }

  public completeBatchSegment(): void {
    this.terminalBatchJobService
      .createNew(this.newBatchJob(WorkSegmentCommand.COMPLETE))
      .subscribe({
        next: (_) => {
          this.toastService.info('Batch Completed.')
          this.router.navigate(['../../../'], {
            relativeTo: this.route,
            queryParamsHandling: 'merge',
          })
        },
        error: (err) => {
          this.toastService.error('Failed to complete batch!', err)
          this.reloadWorkBatch()
        },
      })
  }

  public abortBatch(event: Event): void {
    this.terminalBatchService.delete(this.terminalBatch).subscribe({
      next: (_) =>
        this.router.navigate(['../../'], { relativeTo: this.route, queryParamsHandling: 'merge' }),
      error: (err) =>
        this.toastService.error('Failed to abort batch ' + this.terminalBatch.workBatch.id, err),
    })
  }

  public canAbort(ctx: TerminalWorkBatchCtx): boolean {
    // Todo should we allow aborting while PREPARING?
    return (
      ctx.terminalBatch.workBatch.state === WorkBatchState.PREPARING ||
      ctx.terminalBatch.workBatch.state === WorkBatchState.PREPARED
    )
  }

  /***************************************************************************
   *                                                                         *
   * Public API: Package                                                     *
   *                                                                         *
   **************************************************************************/

  public switchToPackageView() {
    this.selectedTabIndex = 1
  }

  public finalizesPackaging(segment: WorkBatchSegment) {
    return segment.packaging?.mode === WorkBatchSegmentPackagingMode.FINAL
  }

  public isPackagingOk(segment: WorkBatchSegment, packages: Package[]): boolean {
    return this.finalizesPackaging(segment)
      ? !this.isManualCheckInNecessary() && WorkOrderStorageLocationUtil.hasPackages(packages)
      : segment.packaging?.mode !== WorkBatchSegmentPackagingMode.DISABLED
  }

  public getPackagingToolTip(workBatchSegment: WorkBatchSegment, packages: Package[]) {
    if (!this.isPackagingOk(workBatchSegment, packages)) {
      if (!WorkOrderStorageLocationUtil.hasPackages(packages)) {
        return this.translate.instant('context.amount')
      } else if (
        !WorkOrderStorageLocationUtil.hasCurrentPackageLocation(this.storageOverview ?? undefined)
      ) {
        return (
          this.translate.instant('shopfloor.storage.location') +
          ' | ' +
          this.translate.instant(
            'shopfloor.storage.packageCheckinMode.' +
              this.storageOverview?.baseLocation?.packageCheckinMode
          )
        )
      }
    }
    return ''
  }

  public isManualCheckInNecessary(): boolean {
    return WorkOrderStorageLocationUtil.isManualCheckInNecessary(this.storageOverview ?? undefined)
  }

  public reloadStorageOverview(): void {
    this.initStorageOverview()
  }

  packagesDisabled(ctx: TerminalWorkBatchCtx, workBatchSegment: WorkBatchSegment): boolean {
    return !this.hasStarted(ctx) || !this.packageModeEnabled(workBatchSegment)
  }

  packageModeEnabled(workBatchSegment: WorkBatchSegment): boolean {
    return workBatchSegment.packaging.mode !== WorkBatchSegmentPackagingMode.DISABLED
  }

  packageClick(pack: WorkItemPack): void {
    if (pack) {
      this.dialog.open(TerminalOrderPackageDetailDialogueComponent, {
        maxWidth: '95vw',
        maxHeight: '95vw',
        width: '95vw',
        height: '95vh',
        data: <TerminalOrderPackageDialogueOptions>{
          pkgId: pack.packageId,
        },
      })
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API: Batch Tracing                                               *
   *                                                                         *
   **************************************************************************/

  public openBatchTracingConfDetail(): void {
    const ctx = this.batchTracingCtx
    const tracedMaterials = ctx?.materials
    if (tracedMaterials?.length && tracedMaterials.length > 0) {
      this.matDialog
        .open(TracedMaterialBrowserDialogComponent, {
          maxWidth: '60vw',
          maxHeight: '90vh',
          width: '60vw',
          height: '90vh',
          disableClose: true,
          data: <ITracedMaterialBrowserDialogOptions>{
            tracedMaterials,
          },
        })
        .afterClosed()
        .subscribe(() => {
          const batchId = this.workBatchId$.getValue()
          const wcId = this.workCenterId$.getValue()
          if (batchId && wcId) {
            this.loadTracedMaterials(batchId, wcId)
          }
        })
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API: WorkOrder                                                   *
   *                                                                         *
   **************************************************************************/

  public hasHints(ctx: TerminalWorkBatchCtx): boolean {
    return this.hasNote(ctx) || this.hasProductionCodes(ctx)
  }

  public hasNote(ctx: TerminalWorkBatchCtx): boolean {
    return ctx.terminalBatch.workOrder.note != null
  }

  public hasProductionCodes(ctx: TerminalWorkBatchCtx): boolean {
    return ctx.terminalBatch.productionCodes?.length > 0
  }

  public countHints(ctx: TerminalWorkBatchCtx): number {
    return ctx.terminalBatch.productionCodes.length + (this.hasNote(ctx) ? 1 : 0)
  }

  /***************************************************************************
   *                                                                         *
   * Public API: General                                                     *
   *                                                                         *
   **************************************************************************/

  public openDetail(item: WorkItem): void {
    this.matDialog.open(TerminalWorkItemDialogComponent, {
      maxWidth: '95vw',
      maxHeight: '95vh',
      width: '95vw',
      height: '95vh',
      data: <ITerminalWorkItemDialogOptions>{
        item: item,
        controlTerminal: this.controlTerminal,
        workCenterId: this.workCenterId$.getValue(),
        printingEnabled: this.canProvideItemLabels(this.terminalWorkBatchCtx),
      },
    })
  }

  public transferSymbol(segment: WorkBatchSegment): string {
    switch (segment?.transferState) {
      case WorkTransferState.OPEN:
        return '?'
      case WorkTransferState.SENDING:
        return '...'
      case WorkTransferState.CONFIRMED:
        return 'OK'
      case WorkTransferState.FAILED:
        return '!'
      default:
        return '?'
    }
  }

  /***************************************************************************
   *                                                                         *
   * Polling                                                                 *
   *                                                                         *
   **************************************************************************/

  public startPreparationPolling() {
    const hasBeenPrepared$ = this.terminalBatch$.pipe(
      filter((terminalBatch) => this.hasBeenPrepared(terminalBatch?.workBatch))
    )

    this.pollBatchUntil(hasBeenPrepared$)
  }

  public startTransferPolling() {
    const hasBeenTransferred$ = this.workBatchSegment$.pipe(
      filter((segment) => this.hasBeenTransferred(segment))
    )

    this.pollBatchUntil(hasBeenTransferred$)
  }

  public startItemLabelPreparationPolling() {
    const hasBeenPrepared$ = this.workBatchSegment$.pipe(
      filter((segment) => this.itemLabelsPrepared(segment))
    )

    this.pollBatchUntil(hasBeenPrepared$)
  }

  public pollBatchUntil(terminationSubject: Observable<any>): void {
    this.log.debug('Setting up batch polling with predicate')
    this.stopBatchPolling()
    this.log.debug('Starting batch polling...')

    this.batchPoller$ = timer(500, 1500)
      .pipe(
        takeUntil(terminationSubject),
        takeUntil(this.batchPollingTerminator$),
        takeUntilDestroyed(this.destroyRef) // used outside injection context
      )
      .subscribe({
        next: () => this.reloadWorkBatch(),
        error: (err) => this.log.error('Failed to start batch polling', err),
        complete: () => this.reloadWorkBatch(),
      })
  }

  public stopBatchPolling(): void {
    this.log.debug('Stopping batch polling...')
    this.batchPollingTerminator$.next()
  }

  protected determinePackageBadgeColor(emptyPackages: number): ThemePalette {
    return emptyPackages === 0 ? 'primary' : 'warn'
  }

  protected shouldHidePackagesBadge(
    ctx: TerminalWorkBatchCtx,
    workBatchSegment: WorkBatchSegment,
    packages: Package[]
  ): boolean {
    return (
      this.isPackagingOk(workBatchSegment, packages) ||
      !this.hasStarted(ctx) ||
      !this.packageModeEnabled(workBatchSegment)
    )
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private newBatchJob(
    command: WorkSegmentCommand,
    options?: WorkSegmentCommandOptions
  ): NewBatchSegmentJob {
    return new NewBatchSegmentJob(
      this.workBatchId$.getValue(),
      this.workStationId$.getValue(),
      this.workCenterId$.getValue(),
      command,
      options
    )
  }

  private initStorageOverview(): void {
    if (this.terminalBatch?.workBatch) {
      this.loadStorageOverview(
        this.terminalWorkBatchCtx.terminalBatch.workBatch.workOrderId,
        this.terminalBatch.workBatch.id
      )
    } else {
      this.storageOverview$.next(null)
    }
  }

  private loadStorageOverview(workOrderId: string, batchId: string): void {
    this.workOrderService.getStorageOverview(workOrderId, batchId).subscribe({
      next: (overview) => this.storageOverview$.next(overview),
      error: (err) => this.toastService.error('Failed to load Storage Overview!', err),
    })
  }

  private loadTracedMaterials(workBatchId: string, workCenterId: string): void {
    this.batchTracingService
      .load([
        BatchTracingFilter.workBatchId(workBatchId),
        BatchTracingFilter.workCenterId(workCenterId),
      ])
      .subscribe({
        next: (tracedMaterials) => {
          this.log.debug('Loaded traced materials:', tracedMaterials)
          this.batchTracingCtx$.next(new BatchTracingCtx(tracedMaterials))
        },
        error: (err) => this.toastService.error('Failed to load traced materials!', err),
      })
  }
}
